import * as Api from '@ViewModels';
import * as React from 'react';
import { AutomationStepCardSchedule } from '../../../components/automation/AutomationStepCard/presentation';

interface ScheduleEditorProps {
	/**
	 * The current schedule configuration for the step.
	 */
	schedule: Api.IAutomationStepSchedule | null | undefined;
	/**
	 * The automation trigger to determine timing options (e.g., birthday, meeting).
	 */
	trigger: Api.IAutomationTrigger | null | undefined;
	/**
	 * The type of automation step (affects schedule options, e.g., ActionItem has due date).
	 */
	stepType: Api.AutomationStepType;
	/**
	 * Whether the schedule can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether the component is in a busy/loading state.
	 */
	isBusy?: boolean;
	/**
	 * The step object (needed for legacy component compatibility).
	 */
	step: Api.IAutomationStep;
	/**
	 * Index of the step in the automation (used for unique IDs).
	 */
	index?: number;
	/**
	 * Callback when schedule changes. Called with the new schedule.
	 */
	onScheduleChange: (schedule: Api.IAutomationStepSchedule | null) => void;
}

/**
 * ScheduleEditor provides inline timing controls for automation steps.
 *
 * Displays:
 * - "This step happens [X days] [before/after/same day as] [trigger] at [time]"
 * - For ActionItem steps: Due date options (relative day or no due date)
 *
 * Wraps the existing AutomationStepCardSchedule component for use in the
 * canvas-based AutomationFlows editor with proper callback handling.
 */
export function ScheduleEditor({
	schedule,
	trigger,
	stepType,
	canEdit,
	isBusy = false,
	step,
	index = 0,
	onScheduleChange,
}: ScheduleEditorProps) {
	// Local schedule state so the UI updates immediately on user interaction,
	// without waiting for the API round-trip that updates the prop.
	const [localSchedule, setLocalSchedule] = React.useState(schedule);

	// Sync from prop when it changes (e.g., after API refetch or parent re-render).
	React.useEffect(() => {
		setLocalSchedule(schedule);
	}, [schedule]);

	// Create a step wrapper with uuid for generating unique IDs in the legacy component
	const stepWrapper = React.useMemo(() => {
		return {
			...step,
			uuid: step?.id || `schedule-editor-${index}`,
		} as Api.IAutomationStep;
	}, [step, index]);

	const handleRequestSetSchedule = React.useCallback(
		(newSchedule: Api.IAutomationStepSchedule | null) => {
			// Update local state immediately so the schedule UI reflects the change.
			setLocalSchedule(newSchedule);
			onScheduleChange(newSchedule);
		},
		[onScheduleChange]
	);

	// Schedule controls are meaningless without a trigger to anchor timing to.
	if (!trigger) {
		return null;
	}

	return (
		<AutomationStepCardSchedule
			canEdit={canEdit}
			isBusy={isBusy}
			index={index}
			onRequestSetSchedule={handleRequestSetSchedule}
			schedule={localSchedule || undefined}
			step={stepWrapper}
			stepType={stepType}
			trigger={trigger}
		/>
	);
}
